import { Injectable } from '@angular/core';
import { CheckSafeHarborResponse } from '../../models/check-safe-harbor-response';
import { SchedulesDAndHFormModel } from '../../../../models/schedules-d-and-h-form-model';
import { MaximumErpExceededRowData } from '../../models/maximum-erp-exceeded-row-data';
import { ScheduleDFormModel } from '../../../../models/schedule-d-form-model';
import { SafeHarborMobileResultSummary } from '../../models/safe-harbor-mobile-result-summary';
import { AntennaFormModel } from '../../../../models/antenna-form-model';
import { FrequencyFormModel } from '../../../../models/frequency-form-model';
import { tryParseToNumber } from '../../../../../../../../core/functions/string-helpers';
import { MaximumErpExceededResult } from '../../models/maximum-erp-exceeded-result';

@Injectable({
    providedIn: 'root',
})
export class SafeHarborMaximumErpExceededMappingService {
    constructor() {}

    /**
     * Maps the response from the CheckSafeHarbor API to a list of MaximumErpExceededRowData.
     * @param response the response from the CheckSafeHarbor API containing the mobile frequency request results.
     * @param formValue the form value containing the frequencies, antennas, and schedule Ds.
     * @returns an array of MaximumErpExceededRowData objects, each representing a row of data for the maximum ERP exceeded results.
     */
    getMaximumErpExceededRowData(
        response: CheckSafeHarborResponse,
        formValue: SchedulesDAndHFormModel,
    ): MaximumErpExceededRowData[] {
        return response.mobileFrequencyRequestResults
            .filter((result) => result.maximumErpExceededResult !== null)
            .filter(
                (result) =>
                    this.getMobileFrequency(result, formValue).isDeleted !==
                    true,
            )
            .map((result) => {
                const frequency = this.getMobileFrequency(result, formValue);
                const mobileLocation = this.getMobileLocation(
                    frequency,
                    formValue,
                );
                const antenna = this.getCorrespondingAntenna(
                    frequency,
                    formValue,
                );
                const baseStationLocation =
                    this.getCorrespondingBaseStationLocation(
                        result.maximumErpExceededResult!,
                        formValue,
                    );

                return {
                    locationNumber: baseStationLocation.number!,
                    mobileLocationNumber: mobileLocation.number!,
                    antennaNumber: antenna.number!,
                    frequencyLow: tryParseToNumber(frequency.frequencyLow)!,
                    frequencyHigh: tryParseToNumber(frequency.frequencyHigh)!,
                    erp: result.maximumErpExceededResult?.baseStationErp ?? 0,
                    maximumErpAllowed:
                        result.maximumErpExceededResult?.maximumErpAllowed ?? 0,
                };
            });
    }

    private getMobileLocation(
        frequency: FrequencyFormModel,
        formValue: SchedulesDAndHFormModel,
    ): ScheduleDFormModel {
        const mobileLocation = formValue.scheduleDs.find((scheduleD) => {
            return scheduleD.number === frequency?.locationNumber;
        });

        if (!mobileLocation) {
            throw new Error(
                `Could not find mobile location for the given frequency request ID: ${frequency.id}.`,
            );
        }

        return mobileLocation;
    }

    private getMobileFrequency(
        response: SafeHarborMobileResultSummary,
        formValue: SchedulesDAndHFormModel,
    ): FrequencyFormModel {
        const frequency = formValue.frequencies.find((frequency) => {
            return frequency.id === response.frequencyRequestId;
        });

        if (!frequency) {
            throw new Error(
                `Could not find mobile frequency for the given frequency request ID: ${response.frequencyRequestId}.`,
            );
        }

        return frequency;
    }

    private getCorrespondingAntenna(
        frequency: FrequencyFormModel,
        formValue: SchedulesDAndHFormModel,
    ): AntennaFormModel {
        const antenna = formValue.antennas.find((antenna) => {
            return antenna.number === frequency?.antennaNumber;
        });

        if (!antenna) {
            throw new Error(
                `Could not find antenna for the given frequency request ID: ${frequency.id}.`,
            );
        }

        return antenna;
    }

    private getCorrespondingBaseStationLocation(
        maximumErpExceededResult: MaximumErpExceededResult,
        formValue: SchedulesDAndHFormModel,
    ): ScheduleDFormModel {
        const baseStationFrequency = formValue.frequencies.find((frequency) => {
            return frequency.id === maximumErpExceededResult.baseStationId;
        });

        const scheduleD = formValue.scheduleDs.find((scheduleD) => {
            return scheduleD.number === baseStationFrequency?.locationNumber;
        });

        if (!scheduleD) {
            throw new Error(
                `Could not find base station location for the given base station ID: ${maximumErpExceededResult.baseStationId}.`,
            );
        }

        return scheduleD;
    }
}
